import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Tooltip,
  Chip,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  ClickAwayListener,
  Tabs,
  Tab,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Typography, PageHeader } from '@/components/ui';
import type { PageHeaderAction } from '@/components/ui/PageHeader';
import {
  FilterList as FilterIcon,
  GetApp as ExportIcon,
  Visibility as ViewIcon,
  CloudDownload as DownloadIcon,
  Fingerprint as FingerprintIcon,
  AccountBalance as BankIcon,
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Close as CloseIcon,
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { useCommonBreadcrumbs } from '@/utils/breadcrumbs';
import { getBreadcrumbConfig } from '@/config/breadcrumbs';
import { formatCurrency } from '@/utils/formatters';
import { format as formatMomentDate } from '@/utils/time';
import { settlementService } from '@/services/settlementService';
import { SettlementSummaryCards } from '../../components/SettlementSummaryCards';
import { api } from '@/api/client';
import { getStatusColorSatlement, SETTLED_STATUSES } from '@/pages/merchant/Transactions/constants';
import { FilterPanel, ExportDrawer } from '../../components';


export const SettlementPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState("reconciliationsId"); // default
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isExportDrawerOpen, setIsExportDrawerOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any | null>(null);
  const [Settlement, setSettlement] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [SettlementearchTerm, setSettlementearchTerm] = useState('');
  const [merchantSettlementearchTerm, setMerchantSettlementearchTerm] = useState('');
  const [Settlementtats, setSettlementtats] = useState('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50); // Default rows per page

  // Filter panel state
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const [filters, setFilters] = useState({
    searchTerm: '',
    statusFilter: '',
    dateRange: { startDate: '', endDate: '' },
  })

  const [activeTab, setActiveTab] = useState<'settled' | 'unsettled'>('settled');

  // Sorting state
  const [sortField, setSortField] = useState<keyof any>('transaction_date_time');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const STATUSES = {
    UNSETTLED: 'Unsettled',
    HOLD: 'Hold',
    SETTLED: 'settled',
    INITIATED: 'Initiated'
  } as const;


  // Load Settlement with server-side pagination
  useEffect(() => {
    loadSettlement();
    loadSettlementtats();
  }, [page, rowsPerPage, sortField, sortDirection, activeTab]);

  const loadSettlement = async () => {
    setIsLoading(true);
    try {
      const currentPage = page + 1; // Convert 0-based to 1-based page
      const apiResponse = await settlementService.getSettlements(currentPage, rowsPerPage);

      setSettlement(apiResponse.results.data || []);
      setTotalCount(apiResponse.count || 0);
    } catch (error) {
      console.error('Error loading Settlement:', error);
      setSettlement([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSettlementtats = async () => {
    try {
      const stats = await settlementService.getSettlementsStats();
      setSettlementtats(stats);
    } catch (error) {
      console.error('Error loading transaction stats:', error);
      // Keep default stats on error
    }
  };

  // Client-side search filtering (only for search term)
  const filteredSettlement = Array.isArray(Settlement) ? Settlement.filter(txn => {
    // Filter by tab status
    const statusMatch = activeTab === 'settled' 
      ? txn.settlement_status?.toUpperCase() === 'SETTLED'
      : txn.settlement_status?.toUpperCase() === 'HOLD' || txn.settlement_status?.toUpperCase() === 'INITIATED';
    
    if (!statusMatch) return false;
    
    // Filter by search term
    if (!searchTerm) return true;
    return (
      txn.settlement_id?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      txn.merchant_name?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
  }) : [];

  // Breadcrumbs
  const breadcrumbConfig = getBreadcrumbConfig('/Settlement');
  const breadcrumbs = useCommonBreadcrumbs({
    hideBreadcrumbs: breadcrumbConfig.hideBreadcrumbs,
    customBreadcrumbs: breadcrumbConfig.hideBreadcrumbs ? [] : undefined,
  });

  const finalBreadcrumbs = breadcrumbs.length > 0
    ? breadcrumbs
    : [
      {
        label: 'Dashboard',
        href: '/merchant/dashboard',
        onClick: () => navigate('/merchant/dashboard'),
      },
      {
        label: 'Settlements',
        active: true,
      },
    ];

  // Formatters
  const formatSettlementCurrency = (amount: number): string =>
    formatCurrency(amount, 'INR', 'en-IN');

  const formatSettlementDate = (dateString: string): string =>
    formatMomentDate(dateString, 'MMM DD, YYYY HH:mm');

  // Subtitle generator
  // const getSubtitle = () => {
  //   const parts: string[] = [];

  //   if (statusFilter) {
  //     parts.push(`Status: ${STATUSES[statusFilter as typeof SettlementStatus] || statusFilter}`);
  //   }

  //   if (searchTerm) {
  //     parts.push(`Search: "${searchTerm}"`);
  //   }

  //   const total = searchTerm ? filteredSettlement.length : totalCount;
  //   const base = 'Manage payment Settlements';

  //   return parts.length > 0
  //     ? `${base} • ${parts.join(' • ')} (${total} ${total === 1 ? 'transaction' : 'Settlements'})`
  //     : `${base} (${total} ${total === 1 ? 'transaction' : 'Settlements'})`;
  // };


  // Pagination handlers - these will trigger API calls via useEffect
  const handleChangePage = (_event: unknown, newPage: number) => {
    setIsLoading(true);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing page size
  };

  // Sorting handlers - these will trigger API calls via useEffect
  // const handleSort = (field: keyof any) => {
  //   setIsLoading(true);
  //   if (sortField === field) {
  //     setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  //   } else {
  //     setSortField(field);
  //     setSortDirection('asc');
  //   }
  //   setPage(0); // Reset to first page when sorting
  // };

  // Get sort icon for column
  const getSortIcon = (field: keyof any) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />;
  };

  // Table columns configuration
  const columns = [
    { field: 'settlement_id', headerName: 'Reconciliations ID', sortable: true, align: 'left' as const },
    // { field: 'settlement_uuid', headerName: 'Settlement UID', sortable: true, align: 'left' as const },
    { field: 'merchant_name', headerName: 'Merchant Name', sortable: true, align: 'right' as const },
    { field: 'settlement_amount', headerName: 'Amount', sortable: true, align: 'right' as const },
    // { field: 'payment_gateway', headerName: 'Payment Gateway', sortable: true, align: 'left' as const },
    { field: 'settlement_status', headerName: 'Status', sortable: true, align: 'center' as const },
    { field: 'payout_date_time', headerName: 'Created at', sortable: true, align: 'left' as const },
  ];

  // Drawer handlers
  const handleOpenFilterDrawer = (transaction: any) => {
    setSelectedTransaction(transaction);
    setIsFilterDrawerOpen(true);
  };

  const handleCloseFilterDrawer = () => {
    setIsFilterDrawerOpen(false);
    setSelectedTransaction(null);
  };

  // const handleDownloadInovice = async (settlementId: number) => {
  //   console.log('Downloading invoice for settlement ID:', settlementId);

  //   const url = `/merchant/v1/download-settlement/`;
  //   const response = await api.post(url);
  //   console.log('Dashboard chart data response:', response);
  // };


  const handleDownloadInovice = async (settlementId: string) => {
    const url = `/merchant/v1/download-settlement/`;
    try {
      // Ask Axios to treat the response as binary data (Excel file)
      const response = await api.post(
        url,
        { settlement_id: settlementId },
        { responseType: 'blob' } // 👈 important for binary file downloads
      );

      // Create a Blob from the binary Excel data
      const blob = new Blob([response], {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // official Excel MIME type
      });

      // Create a temporary URL for the blob
      const downloadUrl = window.URL.createObjectURL(blob);

      // Create an invisible <a> element to trigger the file download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `invoice-${settlementId}.xlsx`; // 👈 filename for Excel
      document.body.appendChild(link);
      link.click();

      // Clean up temporary elements
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

    } catch (error) {
      console.error('Error downloading Excel invoice:', error);
    }
  };

  const handleFilterPanelOpen = () => {
    setIsFilterPanelOpen(true);
  };

  const handleFilterPanelClose = () => {
    setIsFilterPanelOpen(false);
  };

  const handleFiltersChange = (newFilters: typeof STATUSES) => {
    setFilters(newFilters);
  };

  const handleApplyFilters = async () => {
    setIsLoading(true);
    try {
      setPage(0);

      // Apply date range filter if selected
      if (filters.dateRange?.startDate && filters.dateRange?.endDate) {
        const formatDate = (dateStr: string) => {
          const date = new Date(dateStr);
          return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
        };

        const startDate = formatDate(filters.dateRange.startDate);
        const endDate = formatDate(filters.dateRange.endDate);

        const response = await settlementService.getSettlementDateRangeData(startDate, endDate);
        setSettlement(response.results.data || []);
        setTotalCount(response.count || 0);
        setIsFilterPanelOpen(false);
        return;
      }

      // Apply status filter if selected
      if (filters.statusFilter) {
        const statuses = filters.statusFilter.split(',').filter(Boolean);
        if (statuses.length > 0) {
          const response = await settlementService.getSettlementStatusData(statuses);
          setSettlement(response.results.data || []);
          setTotalCount(response.count || 0);
          setIsFilterPanelOpen(false);
          return;
        }
      }

      // If no filters, reload all transactions
      await loadSettlement();
    } catch (error) {
      console.error('Error applying filters:', error);
      setSettlement([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
      setIsFilterPanelOpen(false);
    }
  };

  const handleClearAllFilters = () => {
    setFilters({
      searchTerm: '',
      statusFilter: '',
      dateRange: { startDate: '', endDate: '' },
    });
    onClearFilters();
  };

  const onClearFilters = () => {
    setIsLoading(true);
    setSearchTerm('');
    setStatusFilter('');
    setPage(0);
    loadSettlement();
    setStatusFilter('reconciliationsId');
    setSettlementearchTerm('');
  };

  // Search handler: fetch Settlement by search terms
  const onSearchTermChange = async () => {
    const transactionId = SettlementearchTerm.trim();
    const merchantId = merchantSettlementearchTerm.trim();

    if (transactionId || merchantId) {
      try {
        const response = await settlementService.getSettlementData(
          transactionId || undefined,
          merchantId || undefined
        );

        // Update state - this will trigger re-render
        setSettlement(response.results.data || []);
        setTotalCount(response.count || 0);
        setSearchTerm(transactionId || merchantId);
      } catch (error) {
        setSettlement([]);
        console.error('Fetch error:', error);
      }
    } else {
      setSettlement([]);
      setSearchTerm('');
    }
  };

  const formatAmountDisplay = (value: unknown): string => {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }
    const numeric =
      typeof value === 'number'
        ? value
        : Number.isNaN(Number(value))
          ? null
          : Number(value);

    if (numeric === null) {
      return String(value);
    }

    return formatSettlementCurrency(numeric);
  };


  // Individual search term handlers
  const handleSettlementearchChange = (value: string) => {
    setSettlementearchTerm(value);
  };

  // const handleMerchantSettlementearchChange = (value: string) => {
  //   setMerchantSettlementearchTerm(value);
  // };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSearchTermChange();
    }
  };

  // Header Actions
  const headerActions: PageHeaderAction[] = [
    {
      label: 'Filter',
      icon: <FilterIcon sx={{ fontSize: 16 }} />,
      onClick: () => setIsFilterDrawerOpen(true),
      variant: 'outlined',
      size: 'medium',
    },
    {
      label: 'Export',
      icon: <ExportIcon sx={{ fontSize: 16 }} />,
      onClick: () => setIsExportDrawerOpen(true),
      variant: 'outlined',
      size: 'medium',
    },
  ];
  return (
    <Box>
      <PageHeader
        title="Reconciliations"
        //subtitle={getSubtitle()}
        //breadcrumbs={finalBreadcrumbs}
        //actions={headerActions}
        showDivider={false}
      />

      <SettlementSummaryCards
        stats={Settlementtats}
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Settled" value="settled" />
          <Tab label="Unsettled" value="unsettled" />
        </Tabs>
      </Box>

      <Box
        mb={3}
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={{ xs: 2, md: 2 }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={{ xs: 2, sm: 2 }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          flex={1}
        >

          <FormControl
            variant="outlined"
            size="small"
            sx={{
              minWidth: { xs: '100%', sm: 180 },
              '& .MuiOutlinedInput-root': {
                background: 'white',
              },
            }}
          >
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="reconciliationsId">Reconciliation ID</MenuItem>
            </Select>
          </FormControl>

          {statusFilter === "reconciliationsId" &&
            <TextField
              variant="outlined"
              type="search"
              placeholder="Search"
              value={SettlementearchTerm}
              onChange={(e) => handleSettlementearchChange(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" sx={{ fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                maxWidth: { xs: '100%', sm: 300, md: 300 },
                '& .MuiOutlinedInput-root': {
                  background: 'white',
                },
              }}
            />
          }

          {/* Input Field */}
          {/* {statusFilter === "reconciliationsUId" &&
            <TextField
              variant="outlined"
              type="search"
              placeholder="Search by Reconciliations UID"
              value={merchantSettlementearchTerm}
              onChange={(e) => handleMerchantSettlementearchChange(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" sx={{ fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                maxWidth: { xs: '100%', sm: 300, md: 300 },
                '& .MuiOutlinedInput-root': {
                  background: 'white',
                },
              }}
            />
          } */}

          {/* Clear Filters Button */}
          <Button
            size="small"
            onClick={onClearFilters}
            sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
          >
            Clear Filters
          </Button>
        </Box>


        {/* Filter Controls */}
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={{ xs: 2, sm: 2 }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<ExportIcon sx={{ color: '#17A6DA' }} />}
            onClick={() => setIsExportDrawerOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Add Filters Button */}
      <Button
        ref={filterButtonRef}
        variant="outlined"
        size="small"
        onClick={handleFilterPanelOpen}
        startIcon={<AddIcon sx={{ color: '#17A6DA' }} />}
        endIcon={<FilterIcon sx={{ color: '#17A6DA' }} />}
        sx={{
          minWidth: { xs: '100%', sm: 'auto' },
          textTransform: 'none',
        }}
      >
        Add filters
      </Button>

      {/* Filter Panel */}
      {isFilterPanelOpen && (
        <ClickAwayListener onClickAway={handleFilterPanelClose}>
          <Box>
            <FilterPanel
              open={isFilterPanelOpen}
              anchorEl={filterButtonRef.current}
              onClose={handleFilterPanelClose}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onApplyFilters={handleApplyFilters}
              onClearFilters={handleClearAllFilters}
            />
          </Box>
        </ClickAwayListener>
      )}


      {/* HTML Table with Pagination */}
      <Paper sx={{ width: '100%', overflow: 'hidden', mt: 2 }}>
        <TableContainer sx={{
          maxHeight: { xs: 400, sm: 600 },
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.1)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.3)',
            borderRadius: 4,
          },
        }}>
          <Table stickyHeader sx={{ minWidth: { xs: 800, sm: 'auto' } }}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.field}
                    align={column.align}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      cursor: column.sortable ? 'pointer' : 'default',
                      '&:hover': column.sortable ? { backgroundColor: '#e0e0e0' } : {},
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                      whiteSpace: 'nowrap',
                    }}
                  //onClick={() => column.sortable && handleSort(column.field as keyof any)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {column.headerName}
                      {column.sortable && getSortIcon(column.field as keyof any)}
                    </Box>
                  </TableCell>
                ))}
                <TableCell sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>

              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <CircularProgress size={24} sx={{ mr: 2 }} />
                      <Typography variant="body1" color="textSecondary">
                        Loading Reconciliations...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : filteredSettlement.length === 0 ? (

                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No Reconciliations found matching your search criteria.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredSettlement.map((settlement) => (
                  <TableRow key={settlement.settlement_id} hover>
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', '&:hover .copy-icon': { opacity: 1 } }}>
                        <Typography variant="body2" fontWeight="medium" fontFamily="monospace" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {String(settlement.settlement_id)}
                        </Typography>
                        <Tooltip title="Copy to clipboard">
                          <IconButton
                            size="small"
                            onClick={() => navigator.clipboard.writeText(String(settlement.settlement_id))}
                            className="copy-icon"
                            sx={{ ml: 0.5, p: 0.25, opacity: 0, transition: 'opacity 0.2s' }}
                          >
                            <ContentCopyIcon sx={{ fontSize: '15px' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    {/* <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FingerprintIcon fontSize="small" color="action" sx={{ mr: 1, opacity: 0.5, fontSize: { xs: '14px', sm: '16px' } }} />
                        <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {String(settlement.settlement_uuid)}
                        </Typography>
                      </Box>
                    </TableCell> */}
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {String(settlement.merchant_name)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="center" color="success" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {formatSettlementCurrency(Number(settlement.settlement_amount))}
                        </Typography>
                      </Box>
                    </TableCell>
                    {/* <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BankIcon fontSize="small" color="primary" sx={{ mr: 1, opacity: 0.7, fontSize: { xs: '14px', sm: '16px' } }} />
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {settlement.payment_gateway && typeof settlement.payment_gateway === 'object' && 'payment_gateway_name' in settlement.payment_gateway
                            ? String((settlement.payment_gateway as any).payment_gateway_name)
                            : 'N/A'
                          }
                        </Typography>
                      </Box>
                    </TableCell> */}
                    <TableCell align="center" sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Tooltip title={String(settlement.settlement_status)}>
                          <Chip
                            label={SETTLED_STATUSES[settlement.settlement_status as TransactionStatus]}
                            size="small"
                            color={getStatusColorSatlement(settlement.settlement_status as TransactionStatus) as 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
                            variant="outlined"
                            sx={{
                              fontWeight: 'normal',
                              fontSize: { xs: '0.625rem', sm: '0.75rem' },
                              borderRadius: '12px',
                              '& .MuiChip-label': { px: 1 },
                            }}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                    }}>
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {formatSettlementDate(String(settlement.settlement_date))}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                    }}>

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenFilterDrawer(settlement)}
                          title="View Details"
                          sx={{ padding: { xs: '4px', sm: '8px' } }}
                        >
                          <ViewIcon fontSize="small" sx={{ fontSize: { xs: '16px', sm: '20px' } }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleDownloadInovice(settlement.settlement_uuid)}
                          title="downlaod Invoice"
                          sx={{ padding: { xs: '4px', sm: '8px' } }}
                        >
                          <DownloadIcon fontSize="small" sx={{ fontSize: { xs: '16px', sm: '20px' } }} />
                        </IconButton>
                      </Box>



                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={searchTerm ? filteredSettlement.length : totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
        />
      </Paper>

      {/* Transaction Details Drawer */}
      <Drawer
        anchor="right"
        open={isFilterDrawerOpen}
        onClose={handleCloseFilterDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 900 },
            padding: { xs: 1, sm: 2 },
            pt: { xs: 8, sm: 10 },
          },
        }}
      >
        {selectedTransaction ? (
          <Box display="flex" flexDirection="column" gap={2}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'right' }}>
              <label onClick={handleCloseFilterDrawer} style={{ cursor: "pointer" }}>
                <CloseIcon style={{ color: "#17A6DA", fontSize: "30px" }} />
              </label>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell colSpan={5} sx={{ fontWeight: 700 }}>
                      Settled Transactions
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Transaction ID</TableCell>
                    <TableCell >Amount</TableCell>
                    <TableCell >Service Charge</TableCell>
                    <TableCell >Service Tax</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(selectedTransaction.settled_transactions) &&
                    selectedTransaction.settled_transactions.length > 0 ? (
                    selectedTransaction.settled_transactions.map(
                      (item: any, index: number) => (
                        <TableRow key={item.transaction_id ?? index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {item.transaction_id ?? 'N/A'}
                          </TableCell>
                          <TableCell >
                            {formatAmountDisplay(item.transaction_amount)}
                          </TableCell>
                          <TableCell >
                            {formatAmountDisplay(item.service_charge)}
                          </TableCell>
                          <TableCell >
                            {formatAmountDisplay(item.service_tax)}
                          </TableCell>
                        </TableRow>
                      )
                    )
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No settled transactions available.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="textSecondary">
              Select a settlement to view its details.
            </Typography>
          </Box>
        )}

      </Drawer>

      {/* Export Drawer */}
      <ExportDrawer
        open={isExportDrawerOpen}
        onClose={() => setIsExportDrawerOpen(false)}
      />
    </Box>
  );
};

export default SettlementPage;